import { useTranslation } from 'react-i18next';
import '../../styles/components/sidebar/switchShop.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useState, useMemo, useEffect } from 'react';
import { updateProfile } from '../../store/reducers/userSlice';
import AddNewShop from './AddNewShop';
import SquareGrayButton from '../../ui/buttons/SquareGrayButton';
import { arrowRightThinSVG, plusSignSVG } from '../../lib/allCustomSVGs';
import { fetchAppInit } from '../../lib/api/app-init';
import { useNavigate } from 'react-router-dom';
import { HOME_ROUTE } from '../../lib/constants/route';
import {
  setIsSwitchShopPopupOpen,
  setIsPaymentPopupOpen,
  setIsPaymentPopupFromBrandLimit,
} from '../../store/reducers/uiSlice';
import { setMessage } from '../../store/reducers/appSlice';
import BrandLimitUpgradePopup from './BrandLimitUpgradePopup';
import { request_package_subscription } from '../../lib/api/ai-tracker/subscriptions/make_package_subscriptions_api_call';
import { getCheckoutFailureMessage } from '../../lib/api/ai-tracker/subscriptions/subscription-error-message';
import { createShop } from '../../lib/api/shop/create_shop';

interface SwitchShopProps {
  onSwitchingChange?: (isSwitching: boolean) => void;
}

const SwitchShop = ({ onSwitchingChange }: SwitchShopProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userProfile = useSelector((state: RootState) => state.user.profile);
  const taraSubscriptionDetails: any = useSelector(
    (state: RootState) => state.user?.shop?.taraSubscriptionDetails
  );
  const taraTrialDetails = useSelector(
    (state: RootState) => state.user?.shop?.taraTrialDetails
  ) || { is_trial: false, trial_available_days: undefined };

  const isActiveTrialWithDays =
    !!taraTrialDetails?.is_trial &&
    taraTrialDetails?.trial_available_days != null &&
    taraTrialDetails.trial_available_days > 0;

  const allShops = userProfile?.all_shops;
  const activeShopSlug = userProfile?.active_shop_slug;
  const activeShop = allShops?.find((shop) => shop.slug === activeShopSlug);

  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSwitching, setIsSwitching] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isUpgradePopupOpen, setIsUpgradePopupOpen] = useState(false);
  const [switchingShopSlug, setSwitchingShopSlug] = useState<string | null>(
    null
  );
  const [viewMode, setViewMode] = useState<'shops' | 'add-shop'>('shops');
  const [shopsPerPage, setShopsPerPage] = useState(3);

  //* Calculate shops per page based on screen width
  useEffect(() => {
    const calculateShopsPerPage = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth > 1500) {
        return 6;
      } else if (screenWidth >= 788) {
        return 4;
      } else if (screenWidth >= 480) {
        return 3;
      } else {
        return 2;
      }
    };

    setShopsPerPage(calculateShopsPerPage());

    const handleResize = () => {
      setShopsPerPage(calculateShopsPerPage());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  //* Notify parent component when switching state changes
  useEffect(() => {
    onSwitchingChange && onSwitchingChange(isSwitching);
  }, [isSwitching, onSwitchingChange]);

  //* Handle brand limit reached - close switch shop popup and open subscription popup
  const handleBrandLimitReached = () => {
    dispatch(setIsSwitchShopPopupOpen(false));
    // Set flag to indicate popup is opened from brand limit
    dispatch(setIsPaymentPopupFromBrandLimit(true));
    // Small delay to ensure smooth transition between popups
    setTimeout(() => {
      dispatch(setIsPaymentPopupOpen(true));
    }, 300);
  };

  //* Handle direct upgrade flow (+5 seats)
  const handleConfirmUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const currentPackage = taraSubscriptionDetails?.package_slug;
      const currentSeatCount = parseInt(
        taraSubscriptionDetails?.purchased_shops?.seat_count || '0'
      );

      if (!currentPackage) {
        throw new Error('No active package found');
      }

      //* 1. Upgrade subscription (+5 seats)
      const upgradeResult = await request_package_subscription(
        currentPackage,
        currentSeatCount + 5
      );
      const upgradeRes = upgradeResult.ok ? upgradeResult.data : false;

      if (upgradeRes) {
        //* Check if it redirects to checkout
        if (
          upgradeRes?.payment_details?.payment_details?._links?.checkout?.href
        ) {
          window.location.href =
            upgradeRes.payment_details.payment_details._links.checkout.href;
          return;
        }

        //* 2. Automatically create new brand
        const tempName = `${userProfile?.email}'s shop`;
        const createRes = await createShop(tempName);

        if (createRes.success && createRes.data) {
          dispatch(
            updateProfile({
              active_shop_slug: createRes.data.slug,
            })
          );
          setIsUpgradePopupOpen(false);
          dispatch(setIsSwitchShopPopupOpen(false));
          navigate(HOME_ROUTE);
          fetchAppInit(true);
        } else {
          dispatch(
            setMessage({
              message:
                createRes.message || 'Brand creation failed after upgrade',
              messageType: 'error',
            })
          );
        }
      } else {
        dispatch(
          setMessage({
            message: getCheckoutFailureMessage(
              upgradeResult.ok ? undefined : upgradeResult.message,
              t('Checkout-StartFailed')
            ),
            messageType: 'error',
          })
        );
      }
    } catch (error: any) {
      console.error('Upgrade flow error:', error);
      dispatch(
        setMessage({
          message: error.message || 'An error occurred during upgrade',
          messageType: 'error',
        })
      );
    } finally {
      setIsUpgrading(false);
    }
  };

  //* Handle Add New Brand button click - check limit first (skip limit for active trial with days remaining)
  const handleAddNewBrandClick = () => {
    const isAgency = userProfile?.role === 'agency';
    const seatCount = parseInt(
      taraSubscriptionDetails?.purchased_shops?.seat_count || '0'
    );
    const shopsCount = allShops?.length || 0;

    if (
      isAgency &&
      shopsCount >= seatCount &&
      !isActiveTrialWithDays
    ) {
      setIsUpgradePopupOpen(true);
    } else {
      setViewMode('add-shop');
    }
  };

  // *Filter shops based on search query
  const filteredShops = useMemo(() => {
    if (!allShops) return [];
    if (!searchQuery.trim()) {
      const activeShop = allShops.find((shop) => shop.slug === activeShopSlug);
      const otherShops = allShops.filter(
        (shop) => shop.slug !== activeShopSlug
      );
      return activeShop ? [activeShop, ...otherShops] : allShops;
    }

    const matchingShops = allShops.filter((shop) =>
      shop.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const activeShop = matchingShops.find(
      (shop) => shop.slug === activeShopSlug
    );
    const otherMatchingShops = matchingShops.filter(
      (shop) => shop.slug !== activeShopSlug
    );

    return activeShop ? [activeShop, ...otherMatchingShops] : matchingShops;
  }, [allShops, searchQuery, activeShopSlug]);

  const handleShopSwitch = async (shopSlug: string) => {
    if (isSwitching || shopSlug === activeShopSlug) return;

    setIsSwitching(true);
    setSwitchingShopSlug(shopSlug);

    setTimeout(() => {
      try {
        dispatch(
          updateProfile({
            active_shop_slug: shopSlug,
          })
        );
        navigate(HOME_ROUTE);
        //* Fetch app init to update the state after switching shop
        fetchAppInit(true);
      } catch (error) {
        console.error('Error switching shop:', error);
      }
    }, 2000); //? 2 seconds delay cz to show the switching shop animation & prevent state update queueing delay
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setCurrentPage(0);
  };

  const totalPages = Math.ceil(filteredShops.length / shopsPerPage);
  const startIndex = currentPage * shopsPerPage;
  const endIndex = startIndex + shopsPerPage;
  const currentShops = filteredShops.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (!allShops || allShops.length === 0) {
    return null;
  }

  if (viewMode === 'add-shop') {
    return (
      <div className='switchShop'>
        <div className='switchShop__header'>
          <div className='switchShop__header-button'>
            <SquareGrayButton
              onClick={() => setViewMode('shops')}
              type='button'
              icon={arrowRightThinSVG}
              normal={true}
              classes='rotate-180-Deg'
            />
          </div>
          <h2>{t('AddNewShop')}</h2>
        </div>
        <AddNewShop onBrandLimitReached={handleBrandLimitReached} />
      </div>
    );
  }

  return (
    <div className='switchShop'>
      <div className='switchShop__titles'>
        <h2>{t('SwitchShop')}</h2>
        <p>{t('SwitchShop-subtext')}</p>
      </div>

      <div className='switchShop__add-shop-button'>
        <button
          className='switchShop__add-shop-btn'
          onClick={handleAddNewBrandClick}
          type='button'
          disabled={isSwitching}
        >
          {plusSignSVG} {t('AddNewShop')}
        </button>
      </div>

      <BrandLimitUpgradePopup
        isOpen={isUpgradePopupOpen}
        onClose={() => setIsUpgradePopupOpen(false)}
        onConfirm={handleConfirmUpgrade}
        isLoading={isUpgrading}
      />

      <div className='switchShop__grid-container'>
        <div className='switchShop__current-shop'>
          <div className='switchShop__current-shop-label'>
            <span>{t('SwitchShop-current-shop')}</span>
          </div>
          <div className='switchShop__current-shop-card'>
            <div className='switchShop__current-shop-avatar'>
              {activeShop?.name?.charAt(0)?.toUpperCase() || 'S'}
            </div>
            <div className='switchShop__current-shop-info'>
              <span className='switchShop__current-shop-name'>
                {activeShop?.name || t('SwitchShop-unknown-shop')}
              </span>
              <span className='switchShop__current-shop-url'>
                {activeShop?.webshop_url || ''}
              </span>
            </div>
            <div className='switchShop__current-shop-badge'>
              {t('SwitchShop-active-badge')}
            </div>
          </div>
        </div>

        <div className='switchShop__available-shops'>
          <div className='switchShop__available-shops-label'>
            <span>{t('SwitchShop-available-shops')}</span>
            {searchQuery && (
              <span className='switchShop__search-results'>
                ({filteredShops.length} of {allShops.length})
              </span>
            )}
          </div>

          <div className='switchShop__search-container'>
            <div className='switchShop__search-input-wrapper'>
              <input
                type='text'
                placeholder={t('SwitchShop-search-placeholder')}
                value={searchQuery}
                onChange={handleSearchChange}
                className='switchShop__search-input'
                disabled={isSwitching}
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className='switchShop__search-clear'
                  type='button'
                  disabled={isSwitching}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {filteredShops.length === 0 ? (
            <div className='switchShop__no-results'>
              <span>{t('SwitchShop-no-results', { query: searchQuery })}</span>
            </div>
          ) : (
            <>
              <div className='switchShop__grid'>
                {currentShops.map((shop) => (
                  <div
                    key={shop.id}
                    className={`switchShop__shop-card ${
                      shop.slug === activeShopSlug && !isSwitching
                        ? 'active'
                        : ''
                    } ${
                      isSwitching && switchingShopSlug === shop.slug
                        ? 'switching'
                        : ''
                    } ${isSwitching ? 'disabled' : ''}`}
                    onClick={() => handleShopSwitch(shop.slug)}
                  >
                    <div className='switchShop__shop-avatar'>
                      {shop.name.charAt(0).toUpperCase()}
                    </div>
                    <div className='switchShop__shop-info'>
                      <span className='switchShop__shop-name'>{shop.name}</span>
                      <span className='switchShop__shop-url'>
                        {shop.webshop_url}
                      </span>
                    </div>
                    {shop.slug === activeShopSlug && (
                      <div className='switchShop__shop-check'>✓</div>
                    )}
                    {isSwitching && switchingShopSlug === shop.slug && (
                      <div className='switchShop__shop-loading'>
                        <div className='switchShop__loading-spinner'></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className='switchShop__pagination'>
                  <button
                    className='switchShop__pagination-btn'
                    onClick={goToPrevPage}
                    disabled={currentPage === 0 || isSwitching}
                  >
                    ‹
                  </button>

                  <div className='switchShop__pagination-dots'>
                    {Array.from({ length: totalPages }, (_, index) => (
                      <button
                        key={index}
                        className={`switchShop__pagination-dot ${
                          index === currentPage ? 'active' : ''
                        }`}
                        onClick={() => goToPage(index)}
                        disabled={isSwitching}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    className='switchShop__pagination-btn'
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages - 1 || isSwitching}
                  >
                    ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SwitchShop;
